import React from 'react';
import { Container, Typography, Box, Paper, Button, Avatar } from '@mui/material';
import { CheckCircle, Home, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import SEOErrorBoundary from '../components/SEOErrorBoundary';

const RegistrationSuccess: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const handleBackToHome = () => {
    const currentLanguage = i18n.language === 'en' ? 'en' : 'es';
    const homeRoute = t('routes.home');
    navigate(`/${currentLanguage}${homeRoute}`);
  };

  return (
    <>
      <SEOErrorBoundary fallbackTitle="Registration Successful - AlertaLegal" fallbackDescription="Your registration for legal publication alerts has been successful.">
        <SEO
          title={t('registrationSuccess.title')}
          description={t('registrationSuccess.description')}
          hreflangEnabled={true}
        />
      </SEOErrorBoundary>
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Paper 
            elevation={3} 
            sx={{ p: 6 }}
            role="status"
            aria-live="polite"
            aria-labelledby="success-header"
            aria-describedby="success-description info-note"
          >
            {/* Success Icon */}
            <Avatar
              sx={{
                bgcolor: 'success.main',
                width: 80,
                height: 80,
                margin: '0 auto',
                mb: 3,
              }}
              aria-label={t('registrationSuccess.header')}
            >
              <CheckCircle sx={{ fontSize: 48 }} aria-hidden="true" />
            </Avatar>

            {/* Main Header */}
            <Typography 
              variant="h4" 
              component="h1" 
              id="success-header"
              gutterBottom 
              sx={{ fontWeight: 'bold', color: 'primary.main' }}
            >
              ✨ {t('registrationSuccess.header')}
            </Typography>

            {/* Email confirmation message */}
            <Typography 
              variant="body1" 
              id="success-description"
              sx={{ fontWeight: 500, mb: 2 }}
            >
              {t('registrationSuccess.checkEmail')}
            </Typography>

            {/* Spam folder reminder */}
            <Typography 
              variant="body2" 
              color="textSecondary" 
              sx={{ mb: 3, fontStyle: 'italic' }}
              aria-describedby="success-description"
            >
              {t('registrationSuccess.checkSpam')}
            </Typography>

            {/* Important note */}
            <Box 
              sx={{ 
                bgcolor: 'info.light', 
                borderRadius: 2,
                p: 3, 
                mb: 4,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2
              }}
              role="region"
              aria-labelledby="info-heading"
              id="info-note"
            >
              <Info 
                sx={{ fontSize: 24, color: 'info.main', mt: 0.1, flexShrink: 0 }} 
                aria-hidden="true"
              />
              <Typography 
                variant="body2" 
                id="info-heading"
                sx={{ fontWeight: 500, color: 'info.dark', lineHeight: 1.5 }}
              >
                {t('registrationSuccess.noMonitoringYet')}
              </Typography>
            </Box>

            {/* Free service reinforcement */}
            <Typography 
              variant="body2" 
              sx={{ 
                textAlign: 'center', 
                color: 'success.main', 
                fontWeight: 500,
                fontSize: '0.9rem',
                mb: 1
              }}
            >
              💰 {t('register.freeService.freeServiceDetail')}
            </Typography>

            {/* Action Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={<Home aria-hidden="true" />}
              onClick={handleBackToHome}
              aria-describedby="success-header success-description"
              sx={{
                mt: 2,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                textTransform: 'none',
                borderRadius: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {t('registrationSuccess.backToHome')}
            </Button>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default RegistrationSuccess;
